export type TimelineData = {
    sportEvent: SportEventData;
    sportEventStatus: SportEventStatus;
    timeline: TimelineEvent[];
};

export enum DESCRIPTION {
    GOAL = "goal",
    PENALTY = "penalty",
    RED_CARD = "red_card",
    NO_GOAL = "no_goal",
    NO_PENALTY = "no_penalty",
    NO_RED_CARD = "no_red_card",
}

export enum GOAL_METHODS {
    PENALTY = "penalty",
    OWN_GOAL = "own_goal",
    FREE_KICK = "free_kick",
}

export enum REGULAR_MATCH_PERIOD {
    FIRST_HALF = "first_half",
    SECOND_HALF = "second_half",
}

export type TimelineEvent = {
    id: number;
    matchTime: number;
    periodType: REGULAR_MATCH_PERIOD | string;
    teamType: TimelineSide;
    time: string;
    type: SUPPORTED_VALUES | UNSUPPORTED_VALUES;
    awayScore?: number;
    homeScore?: number;
    players?: TimelinePlayerInfo[];
    scoringMethod?: GOAL_METHODS;
    stoppageTime?: number;
    varDescription?: DESCRIPTION;
};

export type SportEventData = {
    competitors: {
        away: SportEventCompetitor;
        home: SportEventCompetitor;
    };
    id: number;
    sportEventContext: {
        competition: {
            name: string | COMPETITIONS;
        };
        round: {
            number: string;
        };
        dayName?: string;
    };
    sportRadarId: string;
    startTime: string;
};

export enum COMPETITIONS {
    BUNDESLIGA = "1. Liga",
    EURO = "Europameisterschaft",
    CHAMPIONS_LEAGUE = "Königsklasse",
}

// this is sport_event_status.match_status
export enum MATCH_EVENT_STATUSES {
    NOT_STARTED = "not_started", // The match is scheduled to be played
    STARTED = "started", // The match has begun
    FIRST_HALF = "first_half", // The match is in the first half
    SECOND_HALF = "second_half", // The match is in the second half
    OVERTIME = "overtime", // The match is in overtime
    FIRST_EXTRA = "first_extra", // The match is in the first extra period
    SECOND_EXTRA = "second_extra", // The match is in the second extra period
    AWAITING_PENALTIES = "awaiting_penalties", // Waiting for announcement of penalties
    PENALTIES = "penalties", // Penalties are ongoing
    AWAITING_EXTRA_TIME = "awaiting_extra_time", // Waiting on referee to announce extra time
    INTERRUPTED = "interrupted", // The match has been interrupted
    ABANDONED = "abandoned", // The match has been abandoned
    POSTPONED = "postponed", // The match has been postponed to a future date
    START_DELAYED = "start_delayed", // The match has been temporarily delayed and will be continued
    CANCELLED = "cancelled", // The match has been cancelled and will not be played
    HALFTIME = "half_time", // The match is in halftime
    EXTRA_HALF_TIME = "extra_time_half_time", // The match is in extra time halftime
    ENDED = "ended", // The match has ended
    AET = "aet", // The match has ended after extra time
    AP = "ap", // The match has ended after penalties
}

// this is sport_event_status.status
export enum GAME_EVENT_STATUSES {
    NOT_STARTED = "not_started", // The match is scheduled to be played
    STARTED = "started", // The match has begun
    LIVE = "live", // The match is currently in progress
    POSTPONED = "postponed", // The match has been postponed to a future date
    SUSPENDED = "suspended", // The match has been suspended
    MATCH_ABOUT_TO_START = "match_about_to_start", // The match is about to begin
    DELAYED = "delayed", // The match has been temporarily delayed and will be continued. Typically appears prior to match start
    INTERRUPTED = "interrupted", // The match began, but coverage has stopped for a short time. Note that match scores may not be updated during this period, the last recorded match score will be displayed instead
    CANCELLED = "cancelled", // The match has been cancelled and will not be played
    ENDED = "ended", // The match is over
    CLOSED = "closed", // The match results have been confirmed
    PENDING = "pending", // The match is scheduled to be played
}

export type SportEventStatus = {
    matchStatus: MATCH_EVENT_STATUSES;
    status: GAME_EVENT_STATUSES;
    teamAwayGoals: number;
    teamHomeGoals: number;
    clock?: {
        clockTimePlayed: string;
        stoppageTimePlayed?: string;
    };
};

export type SportEventCompetitor = {
    abbreviation: string;
    id: string;
    name: string;
    qualifier: TimelineSide;
    tipgameLogo: string;
};

type TimelineSide = "home" | "away";

export enum SUPPORTED_VALUES {
    MATCH_STARTED = "match_started",
    MATCH_ENDED = "match_ended",
    BREAK_START = "break_start",
    PERIOD_START = "period_start",
    SCORE_CHANGE = "score_change",
    YELLOW_CARD = "yellow_card",
    RED_CARD = "red_card",
    YELLOW_RED_CARDS = "yellow_red_cards",
    VIDEO_ASSISTANT_REFEREE = "video_assistant_referee",
    VIDEO_ASSISTANT_REFEREE_OVER = "video_assistant_referee_over",
    PENALTY_MISSED = "penalty_missed",
    SUBSTITUTION = "substitution",
    CORNER_KICK = "corner_kick",
    SHOT_ON_TARGET = "shot_on_target",
    FREE_KICK = "free_kick",
    OFFSIDE = "offside",
}

enum UNSUPPORTED_VALUES {
    DECISION_TO_VAR = "decision_to_var",
    DECISITION_TO_VAR_OVER = "decision_to_var_over",
    GOAL_KICK = "goal_kick",
    INJURY = "injury",
    PENALTY_KICK = "penalty_kick",
}

export type TimelinePlayerInfo = {
    displayFullName: string;
    displayLastName: string;
    id: string;
    imageUrl: string | undefined;
    type?: string;
};

export type TimelineEventResponse = TimelineEvent & {
    type: SUPPORTED_VALUES | string;
};
